import { DEBUG, HUD } from '../config/tuning';
import type { Airframe } from '../config/airframes';
import { stallSpeedMs } from '../flight/Aerodynamics';
import type { Autopilot } from '../flight/Autopilot';
import type { FlightState } from '../flight/FlightState';

/**
 * Temporary numeric flight readout (M1 implementation notes).
 *
 * Tuning a flight model without seeing α, β, and load factor is guesswork, so this exists from
 * the first hour of M1. It is replaced wholesale by the vector HUD at M2 — deliberately plain,
 * so nobody mistakes it for the real instrument.
 *
 * Text is rebuilt at DEBUG.TEXT_UPDATE_HZ, not per frame: building strings allocates, and this
 * panel must not be what causes a hitch.
 */

const RAD_TO_DEG = 180 / Math.PI;

const PANEL_STYLE = `
  position: fixed; top: 12px; right: 12px; z-index: 10;
  font: 11px/1.5 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.04em; text-transform: uppercase;
  color: ${HUD.colour}; background: rgba(4, 10, 12, 0.72);
  border: 1px solid rgba(63, 232, 216, 0.35); border-radius: 2px;
  padding: 8px 10px; min-width: 240px; pointer-events: none;
  text-shadow: 0 0 6px rgba(63, 232, 216, 0.35);
`;

export class FlightReadout {
  private readonly root: HTMLDivElement;
  private readonly lines: HTMLDivElement[] = [];
  private readonly airframe: Airframe;
  private lastUpdateMs = 0;
  private visible = true;
  private navX = 0;
  private navZ = 0;
  private navTarget = '';

  constructor(airframe: Airframe, parent: HTMLElement = document.body) {
    this.airframe = airframe;

    this.root = document.createElement('div');
    this.root.setAttribute('style', PANEL_STYLE);

    const title = document.createElement('div');
    title.textContent = 'FLIGHT · M1 READOUT';
    title.style.opacity = '0.65';
    title.style.marginBottom = '4px';
    this.root.appendChild(title);

    for (let i = 0; i < 11; i += 1) {
      const line = document.createElement('div');
      this.lines.push(line);
      this.root.appendChild(line);
    }

    parent.appendChild(this.root);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.root.style.display = visible ? 'block' : 'none';
  }

  toggle(): void {
    this.setVisible(!this.visible);
  }

  dispose(): void {
    this.root.remove();
  }

  /**
   * Navigation reference: where the aircraft is, and where the nearest live target is.
   *
   * The theatre is 24 km across and the readout carried no position at all, which made it
   * impossible to answer "am I near the target yet" from anything but the horizon. That is fine
   * for tuning the flight model — the panel's original job — and useless for flying a mission,
   * which is exactly the gap that made the demo laydown unreachable in practice.
   *
   * Bearing is relative to the nose: negative is left, positive right, so steering onto a target
   * means driving this to zero.
   */
  setNavigation(
    positionM: { x: number; z: number },
    target: { designation: string; positionM: { x: number; y: number; z: number } } | null,
    headingRad: number,
    altitudeM: number,
  ): void {
    this.navX = positionM.x;
    this.navZ = positionM.z;
    if (target === null) {
      this.navTarget = '';
      return;
    }
    const dx = target.positionM.x - positionM.x;
    const dz = target.positionM.z - positionM.z;
    const rangeM = Math.hypot(dx, dz);
    // Heading 0 is north (−z) and increases toward +x, matching the HUD's convention.
    const bearingRad = Math.atan2(dx, -dz);
    let relative = bearingRad - headingRad;
    while (relative > Math.PI) relative -= Math.PI * 2;
    while (relative <= -Math.PI) relative += Math.PI * 2;
    // The flight path angle that would put the nose on it from here.
    const dropM = altitudeM - target.positionM.y;
    const requiredDeg = rangeM > 1 ? -Math.atan2(dropM, rangeM) * RAD_TO_DEG : 0;
    this.navTarget =
      `${target.designation} ${(rangeM / 1000).toFixed(2)}KM ` +
      `BRG ${(relative * RAD_TO_DEG).toFixed(0)}° GLIDE ${requiredDeg.toFixed(1)}°`;
  }

  /** Refresh from the current flight state. Called once per frame; throttled internally. */
  update(state: FlightState, autopilot: Autopilot): void {
    if (!this.visible) return;
    const nowMs = performance.now();
    if (nowMs - this.lastUpdateMs < 1000 / DEBUG.TEXT_UPDATE_HZ) return;
    this.lastUpdateMs = nowMs;

    const airframe = this.airframe;
    const stallMs = stallSpeedMs(airframe, state.airDensityKgM3, Math.max(1, state.loadFactor));
    const overStall = state.airspeedMs - stallMs;

    this.lines[0].textContent = `IAS ${state.airspeedMs.toFixed(1).padStart(6)} M/S    GS ${state.groundSpeedMs.toFixed(1)}`;
    this.lines[1].textContent = `ALT ${state.altitudeM.toFixed(0).padStart(6)} M      VS ${state.verticalSpeedMs.toFixed(1)}`;
    this.lines[2].textContent = `AOA ${(state.alphaRad * RAD_TO_DEG).toFixed(1).padStart(6)}°     AOS ${(state.betaRad * RAD_TO_DEG).toFixed(1)}°`;
    this.lines[3].textContent = `G   ${state.loadFactor.toFixed(2).padStart(6)}       THR ${(state.throttleActual * 100).toFixed(0)}%`;
    this.lines[4].textContent = `HDG ${(state.headingRad * RAD_TO_DEG).toFixed(0).padStart(6)}°     BANK ${(state.bankRad * RAD_TO_DEG).toFixed(0)}°`;
    this.lines[5].textContent = `PIT ${(state.pitchRad * RAD_TO_DEG).toFixed(1).padStart(6)}°     Vs(n) ${stallMs.toFixed(0)} M/S`;
    this.lines[6].textContent = `FUEL ${state.fuelKg.toFixed(1).padStart(5)} KG    INTEG ${(state.integrity * 100).toFixed(0)}%`;
    this.lines[7].textContent = `AP ${autopilot.modeLabel.padEnd(8)}  MARGIN ${overStall >= 0 ? '+' : ''}${overStall.toFixed(0)} M/S`;
    this.lines[8].textContent = this.statusLine(state);
    this.lines[9].textContent = `POS ${this.navX.toFixed(0).padStart(6)} ${this.navZ.toFixed(0).padStart(6)}`;
    this.lines[10].textContent = this.navTarget === '' ? '' : `TGT ${this.navTarget}`;
  }

  private statusLine(state: FlightState): string {
    if (state.structuralFailure) return '*** AIRFRAME FAILURE ***';
    if (state.stalled) return `*** STALL  DEPTH ${(state.stallDepth * 100).toFixed(0)}% ***`;
    if (!state.engineRunning) return '*** FLAMEOUT — GLIDE ***';
    if (state.airspeedMs > this.airframe.vneMs) return '*** OVERSPEED — V_NE ***';
    if (Math.abs(state.loadFactor) > this.airframe.gLimitPos) return '*** OVER-G ***';
    return 'NOMINAL';
  }
}
